import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useIntl } from '@futrli/localisation';
import { CustomersAndSuppliersView } from '@futrli/views/CustomersAndSuppliersView';
import { useApiView } from '@futrli/views/hooks/useApiView';

import { createContext } from '../../../../core/context';
import { usePredictManagementContext } from '../../../../predict-management/context/predict-management-context';
import { buildSortOptions } from '../../../../utils/list-filters/list-filter-utils';
import { SortConfig } from '../../../../utils/list-filters/list-filter-types';
import { useListFilterPersistence } from '../../../../utils/list-filters/use-list-filter-persistence';
import { useFilteredData, FilterStep } from '../../../../utils/list-filters/use-filtered-data';
import { trackEvent } from '../../../../core/tracker';
import { TRACKING_EVENTS } from '../../../../core/constants';

import {
  CASFilters,
  CASExtras,
  CustomersAndSuppliersContextValues,
  PeriodOption,
  TransformedContact,
  TypeShown
} from './types';
export type { TransformedContact };
import { CAS_SORTABLE_COLUMNS, COMPARATORS, DEFAULT_SORT, DEFAULT_PERIOD, LOCAL_STORAGE_KEY } from './constants';
import { deserializeDates } from './helpers';
import { usePeriodOptions } from './usePeriodOptions';
import { useFiltersSidebar } from './useFiltersSidebar';

export const {
  Provider: CustomersAndSuppliersProvider,
  useContext: useCustomersAndSuppliersContext
} = createContext<CustomersAndSuppliersContextValues>();

interface CustomersAndSuppliersContextContainerProps {
  children: ReactNode;
}

export const CustomersAndSuppliersContextContainer = ({ children }: CustomersAndSuppliersContextContainerProps) => {
  const intl = useIntl();

  const { workspaceId = '', organisationId = '' } = useParams();

  const { currency } = usePredictManagementContext();

  const periodOptions: PeriodOption[] = usePeriodOptions();
  const sortOptions = useMemo(() => buildSortOptions(CAS_SORTABLE_COLUMNS, intl), [intl]);

  // -- Core state ------------------------------------------------------------
  const [searchTerm, setSearchTerm] = useState('');
  const [typeShown, setTypeShown] = useState<TypeShown>('all');
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [sortConfig, setSortConfig] = useState<SortConfig>(DEFAULT_SORT);

  // -- Data fetching ---------------------------------------------------------
  const view = useApiView(
    useMemo(
      () => new CustomersAndSuppliersView(workspaceId, organisationId, period),
      [workspaceId, organisationId, period]
    )
  );

  useEffect(() => {
    view.setContactsPeriod(period);
  }, [view, period]);

  const allData = view.state.contacts;
  const isLoading = view.state.isContactsLoading;

  // -- Filters sidebar -------------------------------------------------------
  const currencySymbol = currency || '';

  const {
    filters,
    setFilters,
    activeFilterCount,
    isFiltersSidebarOpen,
    openFiltersSidebar,
    closeFiltersSidebar,
    clearAllFilters,
    draftFilters,
    setDraftFilters,
    applyDraftFilters,
    cancelDraftFilters,
    filterConfig,
    contactOptions,
    handlers
  } = useFiltersSidebar({ allData, currencySymbol });

  // Side panel state for contact deep dive
  const isSidePanelOpen = view.state.isSidePanelOpen;
  const activeContact = view.state.activeContact;
  const isSidePanelLoading = view.state.isContactDetailLoading;

  const handleDeepDive = useCallback(
    (contactId: string, contactType: string) => {
      view.fetchContactDetail(contactId, contactType);
      trackEvent(TRACKING_EVENTS.CLICKED_CUSTOMER_SUPPLIER_DETAIL);
    },
    [view]
  );

  const closeSidePanel = useCallback(() => {
    view.closeSidePanel();
  }, [view]);

  // -- LocalStorage persistence ----------------------------------------------
  const handleRestore = useCallback(
    (restoredFilters: CASFilters, restoredSort: SortConfig | undefined, restoredExtras: CASExtras | undefined) => {
      setFilters(restoredFilters);
      if (restoredSort) {
        setSortConfig(restoredSort);
      }
      if (restoredExtras) {
        setTypeShown(restoredExtras.typeShown);
        setPeriod(restoredExtras.period);
      }
    },
    [setFilters]
  );

  const extras = useMemo<CASExtras>(() => ({ typeShown, period }), [typeShown, period]);

  // Persist filters, sort config, and extra values (type shown and period) to localStorage on change, scoped by organisationId
  useListFilterPersistence<CASFilters, CASExtras>({
    storageKey: LOCAL_STORAGE_KEY,
    organisationId,
    filters,
    sort: sortConfig,
    extras,
    onRestore: handleRestore,
    deserializeDates
  });

  // -- Derived displayed data ------------------------------------------------
  // Build a declarative pipeline of filter steps. useFilteredData applies them
  // in order: search → filter steps → sort.
  const filterSteps = useMemo<FilterStep<TransformedContact>[]>(() => {
    const steps: FilterStep<TransformedContact>[] = [];

    if (typeShown !== 'all') {
      steps.push({ type: 'custom', apply: items => items.filter(item => item.type === typeShown) });
    }

    steps.push(
      { type: 'textMatch', accessor: item => item.name, filter: filters.contacts },
      { type: 'range', accessor: item => item.total_amount_due, filter: filters.totalDue },
      { type: 'range', accessor: item => item.total_amount_overdue, filter: filters.totalOverdue },
      { type: 'range', accessor: item => item.total_amount_invoiced, filter: filters.totalInvoiced },
      { type: 'range', accessor: item => item.unpaid_invoices, filter: filters.invoicesDue },
      { type: 'range', accessor: item => item.average_sale, filter: filters.averageSale },
      { type: 'range', accessor: item => item.average_days_to_pay, filter: filters.daysToPay },
      { type: 'range', accessor: item => item.dependence, filter: filters.dependence },
      { type: 'dateRange', accessor: item => item.last_activity, filter: filters.lastActivity }
    );

    return steps;
  }, [typeShown, filters]);

  const searchAccessors = useMemo(() => [(item: TransformedContact) => item.name], []);

  const displayedData = useFilteredData<TransformedContact>({
    allData,
    searchTerm,
    searchAccessors,
    filterSteps,
    sortConfig,
    comparators: COMPARATORS
  });

  const contextValue: CustomersAndSuppliersContextValues = useMemo(
    () => ({
      allData,
      displayedData,
      isLoading,
      searchTerm,
      setSearchTerm,
      typeShown,
      setTypeShown,
      period,
      setPeriod,
      periodOptions,
      sortConfig,
      setSortConfig,
      sortOptions,
      filters,
      setFilters,
      activeFilterCount,
      isFiltersSidebarOpen,
      openFiltersSidebar,
      closeFiltersSidebar,
      clearAllFilters,
      draftFilters,
      setDraftFilters,
      applyDraftFilters,
      cancelDraftFilters,
      filterConfig,
      contactOptions,
      handlers,
      isSidePanelOpen,
      activeContact,
      isSidePanelLoading,
      handleDeepDive,
      closeSidePanel,
      organisationId,
      workspaceId
    }),
    [
      allData,
      displayedData,
      isLoading,
      searchTerm,
      typeShown,
      period,
      periodOptions,
      sortConfig,
      sortOptions,
      filters,
      setFilters,
      activeFilterCount,
      isFiltersSidebarOpen,
      openFiltersSidebar,
      closeFiltersSidebar,
      clearAllFilters,
      draftFilters,
      setDraftFilters,
      applyDraftFilters,
      cancelDraftFilters,
      filterConfig,
      contactOptions,
      handlers,
      isSidePanelOpen,
      activeContact,
      isSidePanelLoading,
      handleDeepDive,
      closeSidePanel,
      organisationId,
      workspaceId
    ]
  );

  return <CustomersAndSuppliersProvider value={contextValue}>{children}</CustomersAndSuppliersProvider>;
};
